import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Axe,
  BarChart3,
  BookOpen,
  CalendarClock,
  Compass,
  Eye,
  FileQuestion,
  FileText,
  Handshake,
  Layers,
  ListChecks,
  Lock,
  MessageSquare,
  NotebookPen,
  RefreshCw,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  ThumbsDown,
  TriangleAlert,
  UserMinus,
  UserPlus,
  UsersRound,
  Wrench,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { staffApiService } from '../services/staffApi';
import { staffManagerApiService } from '../services/staffManagerApi';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDiscordPlayer } from '../hooks/useDiscordPlayer';
import { StaffCitationsProvider } from '../components/staff/StaffCitationsContext';
import { StaffSection } from '../components/staff/StaffSection';
import { StaffRosterRail } from '../components/staff/StaffRosterRail';
import { StaffProfileHeader } from '../components/staff/StaffProfileHeader';
import { StaffProfileSkeleton } from '../components/staff/StaffProfileSkeleton';
import { StaffStatTiles } from '../components/staff/StaffStatTiles';
import { StaffSignalCards } from '../components/staff/StaffSignalCards';
import { StaffClaimList } from '../components/staff/StaffClaimList';
import { EvidenceText } from '../components/staff/EvidenceText';
import { StaffCitationMix } from '../components/staff/StaffCitationMix';
import { StaffEvidenceLog } from '../components/staff/StaffEvidenceLog';
import { StaffScheduleCard } from '../components/staff/StaffScheduleCard';
import { StaffAvatar } from '../components/staff/StaffAvatar';
import { StaffIdentityStrip } from '../components/staff/StaffIdentityStrip';
import { StaffTicketsCard } from '../components/staff/StaffTicketsCard';
import { StaffHonourList } from '../components/staff/StaffHonourBadge';
import { PlayerProfile } from '../components/PlayerProfile';
import { TeamColourChip } from '../components/staff/manage/TeamColourChip';
import { MembershipExpiryBadge } from '../components/staff/manage/MembershipExpiryBadge';
import { TesseractAccessNotice } from '../components/staff/manage/TesseractAccessNotice';
import { AssignToTeamModal } from '../components/staff/manage/AssignToTeamModal';
import { RemoveFromTeamModal } from '../components/staff/manage/RemoveFromTeamModal';
import { MembershipExpiryModal } from '../components/staff/manage/MembershipExpiryModal';
import { StaffNotesPanel } from '../components/staff/notes/StaffNotesPanel';
import type { StaffProfileResponse, StaffRosterResponse, StaffTicketCountsResponse } from '../types/staff';
import type { StaffCapabilities, StaffDirectoryTeam, StaffMemberSummary, StaffTeam, StaffTeamMember, TesseractUserDetail } from '../types/staffManager';
import { getStaffPerms, STAFF_PANEL_PERMISSION } from '../utils/staffPermissions';
import { formatTenure, isMembershipActive } from '../utils/staffTenure';
import { findTicketPerson, personHonours } from '../utils/staffTickets';
import { cn } from '../utils/cn';

type TabId = 'overview' | 'teams' | 'notes' | 'activity' | 'steward';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'overview', label: 'Overview', icon: Compass },
  { id: 'teams', label: 'Teams & Access', icon: UsersRound },
  { id: 'notes', label: 'Notes', icon: NotebookPen },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'steward', label: 'Steward profile', icon: BookOpen },
];

const humanAction = (action: string) => action.replace(/_/g, ' ');

/** "Grants" drops out below xl (the Groups card below lists them) so the membership table fits without sideways scrolling. */
const COL_GRANTS = 'hidden xl:table-cell';
/** Fixed table layout: sized columns take their width, team and grants share the rest. */
const MEMBERSHIP_COLUMNS: { id: string; label: string; className?: string }[] = [
  { id: 'team', label: 'Team' },
  { id: 'joined', label: 'Joined', className: 'w-[130px]' },
  { id: 'expiry', label: 'Expiry', className: 'w-[150px]' },
  { id: 'grants', label: 'Grants', className: COL_GRANTS },
  { id: 'actions', label: '', className: 'w-[130px] xl:w-[220px]' },
];

const GlanceTile = ({ icon: Icon, label, value, sub, tone = 'text-accent-300 bg-accent-500/15', to }: { icon: React.ComponentType<{ className?: string }>; label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string; to?: string }) => {
  const inner = (
    <>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-400">
        <span className={cn('inline-flex w-6 h-6 items-center justify-center rounded-md', tone)}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-white tabular-nums leading-none">{value}</div>
      {sub && <div className="mt-1.5 text-xs text-glass-500">{sub}</div>}
    </>
  );
  const cls = 'glass-card p-4 min-w-0 block transition-colors';
  return to ? (
    <Link to={to} className={cn(cls, 'hover:border-accent-400/30')}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
};

/** Staff Manager — one member: Tesseract identity & tenure, teams, notes, ARGUS activity and the steward profile. */
export const StaffProfileView = () => {
  const { discordId = '' } = useParams<{ discordId: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getStaffPerms(identity, isDev), [identity, isDev]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { uuid: linkedUuid, username: linkedName } = useDiscordPlayer(discordId);

  const [data, setData] = useState<StaffProfileResponse | null>(null);
  const [stewardError, setStewardError] = useState<string | null>(null);
  const [roster, setRoster] = useState<StaffRosterResponse | null>(() => staffApiService.getCachedRoster());
  const [ticketCounts, setTicketCounts] = useState<StaffTicketCountsResponse | null>(() => staffApiService.getCachedTicketCounts());
  const [summary, setSummary] = useState<StaffMemberSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<StaffCapabilities | null>(() => staffManagerApiService.peek<StaffCapabilities>('capabilities'));
  const [teams, setTeams] = useState<StaffTeam[]>([]);
  const [userDetail, setUserDetail] = useState<TesseractUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ team: StaffTeam; member: StaffTeamMember } | null>(null);
  const [expiryTarget, setExpiryTarget] = useState<{ team: StaffTeam; member: StaffTeamMember } | null>(null);
  const [notesNonce, setNotesNonce] = useState(0);

  const load = useCallback(
    async (force = false) => {
      if (!discordId) return;
      if (force) setRefreshing(true);
      setStewardError(null);
      setSummaryError(null);
      const [profileRes, rosterRes, summaryRes, capRes, teamsRes, ticketsRes] = await Promise.allSettled([
        staffApiService.getProfile(discordId, { force }),
        staffApiService.getRoster().catch(() => null),
        staffManagerApiService.getMember(discordId, { uuid: linkedUuid }),
        staffManagerApiService.getCapabilities({ force }),
        perms.canManage ? staffManagerApiService.getTeams({ force }) : Promise.resolve(null),
        // Best effort: the tickets card and honour pills simply stay off when the bot is unreachable.
        staffApiService.getTicketCounts({ force }).catch(() => null),
      ]);
      if (profileRes.status === 'fulfilled') setData(profileRes.value);
      else setStewardError(profileRes.reason instanceof Error ? profileRes.reason.message : 'Failed to load the steward profile');
      if (rosterRes.status === 'fulfilled' && rosterRes.value) setRoster(rosterRes.value);
      if (ticketsRes.status === 'fulfilled' && ticketsRes.value) setTicketCounts(ticketsRes.value);
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
      else setSummaryError(summaryRes.reason instanceof Error ? summaryRes.reason.message : 'Failed to load the member summary');
      if (capRes.status === 'fulfilled') setCapabilities(capRes.value);
      if (teamsRes.status === 'fulfilled' && teamsRes.value) setTeams(teamsRes.value.teams);
      setLoading(false);
      setRefreshing(false);
    },
    [discordId, linkedUuid, perms.canManage],
  );

  useEffect(() => {
    if (!perms.canView) return;
    setLoading(true);
    setData(null);
    setSummary(null);
    setUserDetail(null);
    void load();
  }, [perms.canView, load]);

  const entry = summary?.tesseract ?? null;
  const displayName = data?.display_name ?? entry?.minecraft_username ?? entry?.discord_username ?? linkedName ?? entry?.username ?? discordId;
  useDocumentTitle(loading ? 'Staff Profile' : `Staff: ${displayName}`);

  const visibleTabs = useMemo(() => TABS.filter((t) => t.id !== 'notes' || perms.canReadNotes), [perms.canReadNotes]);
  const rawTab = searchParams.get('tab') as TabId | null;
  const activeTab: TabId = visibleTabs.some((t) => t.id === rawTab) ? (rawTab as TabId) : 'overview';
  const setTab = (tab: TabId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Groups / roles for the Teams & Access tab (needs manage + Tesseract user.view; best effort).
  useEffect(() => {
    if (activeTab !== 'teams' || !perms.canManage || !entry?.user_id || userDetail) return;
    staffManagerApiService.getUser(entry.user_id).then(setUserDetail).catch(() => undefined);
  }, [activeTab, perms.canManage, entry?.user_id, userDetail]);

  const canEdit = perms.canManage && !!capabilities?.tesseract.connected && capabilities.tesseract.can_edit_teams;
  const isSelf = !!entry && !!capabilities?.tesseract.user_id && entry.user_id.toLowerCase() === capabilities.tesseract.user_id.toLowerCase();
  const mcUuid = entry?.minecraft_uuid ?? linkedUuid ?? null;
  const now = Date.now();
  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);
  const honours = useMemo(() => {
    const person = ticketCounts ? findTicketPerson(ticketCounts, discordId) : null;
    return person ? personHonours(person) : [];
  }, [ticketCounts, discordId]);

  if (!perms.canView) {
    return (
      <div className="glass-card p-8 text-center">
        <p className="text-glass-300">You don't have permission to view the Staff Manager.</p>
        <p className="text-glass-500 text-sm mt-1">
          Ask an admin for the <code>{STAFF_PANEL_PERMISSION}</code> permission.
        </p>
      </div>
    );
  }

  const rail = roster && (
    <StaffRosterRail
      staff={roster.staff}
      activeDiscordId={discordId}
      generatedAt={roster.generated_at}
      className="hidden xl:flex xl:w-72 2xl:w-80 flex-shrink-0 self-start xl:sticky xl:top-4"
    />
  );

  const noteSubject = { discord_id: discordId, uuid: mcUuid, user_id: entry?.user_id ?? null, display_name: displayName };

  const membershipRow = (t: StaffDirectoryTeam) => {
    const team = teamById.get(t.id) ?? null;
    const active = isMembershipActive(t, now);
    const member: StaffTeamMember | null = entry ? { user_id: entry.user_id, username: entry.username, email: entry.email, assigned_at: t.assigned_at, expires_at: t.expires_at, assigned_by: t.assigned_by } : null;
    return (
      <tr key={t.id} className={cn('border-b border-glass-200/10', !active && 'opacity-60')}>
        <td className="px-4 py-3">
          <TeamColourChip team={t} size="md" showDomain />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          {t.assigned_at ? (
            <>
              <div className="text-sm text-white font-semibold tabular-nums">{formatTenure(t.assigned_at, now)}</div>
              <div className="text-[11px] text-glass-500">{format(new Date(t.assigned_at), 'd MMM yyyy')}</div>
            </>
          ) : (
            <span className="text-xs text-glass-500">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          <MembershipExpiryBadge expiresAt={t.expires_at} showPermanent />
        </td>
        <td className={cn('px-4 py-3 text-xs text-glass-400', COL_GRANTS)}>
          {team ? (
            team.groups.length > 0 ? (
              <span className="inline-flex flex-wrap gap-1">
                {team.groups.map((g) => (
                  <span key={g.id} className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-mono bg-glass-100/10 text-glass-300 border border-glass-200/15">
                    {g.name}
                  </span>
                ))}
              </span>
            ) : (
              '—'
            )
          ) : (
            <span className="text-glass-500">—</span>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          {canEdit && active && team && member && !isSelf && (
            <span className="inline-flex flex-wrap justify-end items-center gap-1">
              <button type="button" onClick={() => setExpiryTarget({ team, member })} className="glass-button-sm inline-flex items-center gap-1">
                <CalendarClock className="w-3.5 h-3.5" /> Expiry
              </button>
              <button type="button" onClick={() => setRemoveTarget({ team, member })} className="glass-button-sm inline-flex items-center gap-1 text-red-300 hover:bg-red-500/10">
                <UserMinus className="w-3.5 h-3.5" /> Remove
              </button>
            </span>
          )}
        </td>
      </tr>
    );
  };

  let main: React.ReactNode;
  if (loading) {
    main = <StaffProfileSkeleton />;
  } else if (!data && !entry) {
    main = (
      <div className="glass-card p-6 border-l-4 border-red-400 space-y-2">
        <div className="flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-300">{stewardError || summaryError || 'Profile not found'}</span>
        </div>
        {summaryError && stewardError && <p className="text-xs text-glass-500">{summaryError}</p>}
        <button onClick={() => void load(true)} className="glass-button mt-2">
          Retry
        </button>
      </div>
    );
  } else {
    const profile = data?.profile ?? null;
    const citationCount = Object.keys(data?.citations ?? {}).length;
    const moderation = summary?.activity.moderation ?? null;
    const argus = summary?.activity.argus ?? null;
    const notesSummary = summary?.staff_notes ?? null;
    const windowDays = summary?.activity.window_days ?? 90;

    main = (
      <StaffCitationsProvider citations={data?.citations ?? {}} generatedAt={data?.generated_at ?? Math.floor(now / 1000)}>
        <div className="space-y-4">
          {data ? (
            <StaffProfileHeader data={data} honours={honours} />
          ) : (
            entry && (
              <header className="glass-card relative overflow-hidden p-5 md:p-7">
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-300/70 to-transparent" aria-hidden="true" />
                <div className="flex items-start gap-4">
                  <StaffAvatar discordId={discordId} name={displayName} size="lg" glow />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[11px] font-semibold uppercase tracking-[0.12em] bg-accent-500/20 text-accent-300 border border-accent-500/30">
                        <ShieldCheck className="w-3 h-3" aria-hidden="true" /> Staff
                      </span>
                      <code className="text-xs font-mono text-glass-300">@{entry.discord_username ?? entry.username}</code>
                      <StaffHonourList honours={honours} max={3} />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-white leading-[1.05] tracking-tight">{displayName}</h1>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                      {mcUuid && <PlayerProfile uuid={mcUuid} name={entry.minecraft_username ?? undefined} showUuid={false} inline />}
                      <span className="text-xs text-glass-500">No steward profile — {stewardError ?? 'the bot has not observed this member in tickets yet.'}</span>
                    </div>
                  </div>
                </div>
              </header>
            )
          )}

          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-1 rounded-xl border border-glass-200/20 bg-glass-100/5 p-1 w-fit max-w-full" role="tablist" aria-label="Profile sections">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'inline-flex items-center gap-2 h-9 px-3.5 rounded-lg text-sm font-semibold transition-colors touch-manipulation',
                    active ? 'bg-accent-500/20 text-accent-200 shadow-[inset_0_0_0_1px_rgb(var(--accent-t-400-rgb)/0.35)]' : 'text-glass-400 hover:text-white hover:bg-glass-200/10',
                  )}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  {t.label}
                  {t.id === 'notes' && notesSummary && notesSummary.active > 0 && <span className="text-[10px] font-mono text-amber-300">{notesSummary.active}</span>}
                </button>
              );
            })}
          </div>

          {activeTab === 'overview' && (
            <>
              <StaffIdentityStrip summary={summary} loading={false} error={summaryError} capabilities={capabilities} />
              {data?.evidence && <StaffStatTiles evidence={data.evidence} windowDays={data.window_days} generatedAt={data.generated_at} />}

              <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
                <GlanceTile icon={FileText} label="Notes written" value={moderation?.notes ?? '—'} sub={`player notes · ${windowDays}d`} tone="text-blue-300 bg-blue-500/15" />
                <GlanceTile icon={TriangleAlert} label="Warnings issued" value={moderation?.warnings ?? '—'} sub={`${windowDays}d`} tone="text-yellow-300 bg-yellow-500/15" />
                <GlanceTile icon={Axe} label="Bans issued" value={moderation?.bans ?? '—'} sub={`${windowDays}d`} tone="text-red-300 bg-red-500/15" />
                <GlanceTile
                  icon={Activity}
                  label="ARGUS actions"
                  value={argus?.actions ?? '—'}
                  sub={argus?.last_seen_at ? `last seen ${formatDistanceToNow(new Date(argus.last_seen_at), { addSuffix: true })}` : `${windowDays}d`}
                  tone="text-green-300 bg-green-500/15"
                  to={`/staff/${discordId}?tab=activity`}
                />
                {notesSummary && (
                  <GlanceTile
                    icon={Lock}
                    label="Staff notes"
                    value={notesSummary.active}
                    sub={notesSummary.warnings > 0 ? `${notesSummary.warnings} warning${notesSummary.warnings === 1 ? '' : 's'} · ${notesSummary.commendations} commendation${notesSummary.commendations === 1 ? '' : 's'}` : notesSummary.commendations > 0 ? `${notesSummary.commendations} commendation${notesSummary.commendations === 1 ? '' : 's'}` : 'confidential'}
                    tone={notesSummary.warnings > 0 ? 'text-yellow-300 bg-yellow-500/15' : 'text-amber-300 bg-amber-500/15'}
                    to={`/staff/${discordId}?tab=notes`}
                  />
                )}
              </div>

              {ticketCounts && <StaffTicketsCard data={ticketCounts} discordId={discordId} />}

              {profile ? (
                <>
                  <StaffSignalCards profile={profile} />
                  <StaffSection title="Summary" icon={BookOpen} actions={<button type="button" onClick={() => setTab('steward')} className="glass-button-sm">Full steward profile</button>}>
                    <p className="text-[15px] text-white/90 leading-7">
                      <EvidenceText text={profile.summary} />
                    </p>
                  </StaffSection>
                </>
              ) : (
                data && (
                  <div className="glass-card relative overflow-hidden p-8 text-center">
                    <FileQuestion className="w-8 h-8 mx-auto mb-2 text-accent-300" />
                    <h2 className="text-base font-semibold text-white">No steward profile built yet</h2>
                    <p className="text-sm text-glass-400 mt-1 max-w-md mx-auto">The bot has not seen enough ticket activity from {displayName} to build a profile.</p>
                  </div>
                )
              )}
            </>
          )}

          {activeTab === 'teams' && (
            <>
              {perms.canManage ? <TesseractAccessNotice capabilities={capabilities} needs="role.edit" /> : <TesseractAccessNotice capabilities={capabilities} needs="role.view" />}
              <StaffSection
                title="Team memberships"
                icon={UsersRound}
                count={entry?.teams.filter((t) => isMembershipActive(t, now)).length}
                actions={
                  canEdit && entry && !isSelf ? (
                    <button type="button" onClick={() => setAssignOpen(true)} className="glass-button-sm inline-flex items-center gap-1.5">
                      <UserPlus className="w-3.5 h-3.5" /> Add to team
                    </button>
                  ) : isSelf ? (
                    <span className="text-[11px] text-glass-500">You cannot change your own membership from ARGUS.</span>
                  ) : undefined
                }
                bodyClassName="px-0 md:px-0 pb-0 md:pb-0"
              >
                {!entry ? (
                  <p className="px-4 md:px-5 pb-4 text-sm text-glass-500">{summary?.tesseract_error ?? 'This member is not in the Tesseract directory.'}</p>
                ) : entry.teams.length === 0 ? (
                  <p className="px-4 md:px-5 pb-4 text-sm text-glass-500">No team memberships.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full table-fixed">
                      <thead>
                        <tr className="border-b border-glass-200/15">
                          {MEMBERSHIP_COLUMNS.map((c) => (
                            <th key={c.id} className={cn('text-left text-[11px] uppercase tracking-[0.14em] text-glass-500 font-semibold px-4 py-2.5 whitespace-nowrap', c.className)}>
                              {c.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>{[...entry.teams].sort((a, b) => Number(isMembershipActive(b, now)) - Number(isMembershipActive(a, now)) || (a.assigned_at ?? 0) - (b.assigned_at ?? 0)).map(membershipRow)}</tbody>
                    </table>
                  </div>
                )}
              </StaffSection>

              {(userDetail || perms.canManage) && entry && (
                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))] gap-4">
                  <StaffSection title="Groups" icon={Layers} count={userDetail?.groups.length}>
                    {!userDetail ? (
                      <p className="text-xs text-glass-500">{perms.canManage ? 'Loading from Tesseract…' : 'Requires argus.staff.manage.'}</p>
                    ) : userDetail.groups.length === 0 ? (
                      <p className="text-xs text-glass-500">No groups.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {userDetail.groups.map((g) => (
                          <span key={g.name} className="inline-flex items-center h-6 px-2 rounded-md text-xs font-mono bg-glass-100/10 text-glass-200 border border-glass-200/15" title={g.display_name}>
                            {g.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-glass-500 mt-3">Groups carry ARGUS permission flags. They follow team membership automatically; direct grants are made in the CDU Hub.</p>
                  </StaffSection>
                  <StaffSection title="Tesseract roles" icon={ShieldCheck} count={userDetail?.roles.length}>
                    {!userDetail ? (
                      <p className="text-xs text-glass-500">{perms.canManage ? 'Loading from Tesseract…' : 'Requires argus.staff.manage.'}</p>
                    ) : userDetail.roles.length === 0 ? (
                      <p className="text-xs text-glass-500">No roles.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {userDetail.roles.map((r) => (
                          <span key={r.name} className="inline-flex items-center h-6 px-2 rounded-md text-xs font-semibold bg-accent-500/15 text-accent-300 border border-accent-500/25">
                            {r.display_name}
                          </span>
                        ))}
                      </div>
                    )}
                    {userDetail?.banned && <p className="text-xs text-red-300 mt-3">This account is banned in Tesseract.</p>}
                  </StaffSection>
                </div>
              )}
            </>
          )}

          {activeTab === 'notes' && perms.canReadNotes && (
            <StaffSection title="Staff Manager Notes" icon={Lock} tone="watch" count={notesSummary?.total}>
              <p className="text-[11px] text-glass-500 mb-3">Confidential — visible only to holders of argus.staff.notes.read. Every read and write is audit-logged.</p>
              <StaffNotesPanel subject={noteSubject} canWrite={perms.canWriteNotes} refreshNonce={notesNonce} onLoaded={() => setNotesNonce((n) => n)} />
            </StaffSection>
          )}

          {activeTab === 'activity' && (
            <>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))] gap-4 items-start">
                <StaffSection title="Moderation output" icon={ShieldCheck} count={moderation ? moderation.notes + moderation.warnings + moderation.bans : undefined}>
                  {!moderation ? (
                    <p className="text-xs text-glass-500">No linked Minecraft account, so moderation records cannot be attributed.</p>
                  ) : (
                    <dl className="grid grid-cols-3 gap-3">
                      {[
                        ['Notes', moderation.notes, 'text-blue-300'],
                        ['Warnings', moderation.warnings, 'text-yellow-300'],
                        ['Bans', moderation.bans, 'text-red-300'],
                      ].map(([k, v, tone]) => (
                        <div key={String(k)} className="rounded-lg bg-glass-100/5 border border-glass-200/10 p-3">
                          <dt className="text-[10px] uppercase tracking-[0.14em] text-glass-500">{k}</dt>
                          <dd className={cn('text-2xl font-bold tabular-nums', tone as string)}>{v as number}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  <p className="text-[11px] text-glass-500 mt-3">
                    Last {windowDays} days{moderation?.last_at ? ` · most recent ${formatDistanceToNow(new Date(moderation.last_at), { addSuffix: true })}` : ''}
                    {mcUuid && (
                      <>
                        {' '}
                        ·{' '}
                        <Link to={`/player/${mcUuid}`} className="text-accent-300 hover:underline">
                          player profile
                        </Link>
                      </>
                    )}
                  </p>
                </StaffSection>

                <StaffSection title="ARGUS usage" icon={Activity} count={argus?.actions}>
                  {!argus ? (
                    <p className="text-xs text-glass-500">No Tesseract email known, so ARGUS audit activity cannot be attributed.</p>
                  ) : argus.by_action.length === 0 ? (
                    <p className="text-xs text-glass-500">No ARGUS actions in the last {windowDays} days.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {argus.by_action.slice(0, 10).map((a) => {
                        const max = argus.by_action[0]?.count ?? 1;
                        return (
                          <li key={a.action} className="flex items-center gap-2 text-xs">
                            <span className="w-40 truncate text-glass-300 font-mono" title={a.action}>
                              {humanAction(a.action)}
                            </span>
                            <span className="flex-1 h-1.5 rounded-full bg-glass-100/10 overflow-hidden">
                              <span className="block h-full rounded-full bg-gradient-to-r from-accent-500 to-accent-300" style={{ width: `${Math.max(4, (a.count / max) * 100)}%` }} />
                            </span>
                            <span className="w-8 text-right tabular-nums text-white">{a.count}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {argus?.last_seen_at && <p className="text-[11px] text-glass-500 mt-3">Last ARGUS action {formatDistanceToNow(new Date(argus.last_seen_at), { addSuffix: true })}.</p>}
                </StaffSection>
              </div>

              {argus && argus.recent.length > 0 && (
                <StaffSection title="Recent ARGUS actions" icon={ScrollText} count={argus.recent.length}>
                  <ol className="divide-y divide-glass-200/10">
                    {argus.recent.map((r, i) => (
                      <li key={`${r.timestamp}-${i}`} className="flex items-center justify-between gap-3 py-2 text-xs">
                        <span className="font-mono text-glass-300 truncate">{humanAction(r.action)}</span>
                        <time className="text-glass-500 whitespace-nowrap" title={format(new Date(r.timestamp), 'd MMM yyyy HH:mm:ss')}>
                          {formatDistanceToNow(new Date(r.timestamp), { addSuffix: true })}
                        </time>
                      </li>
                    ))}
                  </ol>
                </StaffSection>
              )}

              {data?.schedule && <StaffScheduleCard schedule={data.schedule} />}
            </>
          )}

          {activeTab === 'steward' && (
            <>
              {!data ? (
                <div className="glass-card p-8 text-center text-sm text-glass-500">{stewardError ?? 'No steward profile for this member.'}</div>
              ) : !profile ? (
                <div className="glass-card relative overflow-hidden p-10 text-center">
                  <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_0%,rgb(var(--accent-t-500-rgb)/0.12),transparent_70%)]" aria-hidden="true" />
                  <FileQuestion className="relative w-10 h-10 mx-auto mb-3 text-accent-300" />
                  <h2 className="relative text-lg font-semibold text-white">No profile built yet</h2>
                  <p className="relative text-sm text-glass-400 mt-1 max-w-md mx-auto leading-relaxed">
                    The steward bot has not seen enough ticket activity from {displayName} to build a profile, so it has no handoff guidance for them yet. Evidence counts and the reply-hour pattern update as activity accrues.
                  </p>
                </div>
              ) : (
                <>
                  <StaffSignalCards profile={profile} />
                  <StaffSection title="Summary" icon={BookOpen}>
                    <p className="text-[15px] text-white/90 leading-7">
                      <EvidenceText text={profile.summary} />
                    </p>
                  </StaffSection>

                  <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] gap-4 items-start">
                    <div className="space-y-4 min-w-0">
                      <StaffSection title="Strengths" icon={Sparkles} count={profile.strengths.length} tone="good">
                        <StaffClaimList items={profile.strengths} tone="good" />
                      </StaffSection>
                      <StaffSection title="Good at" icon={ListChecks} count={profile.good_at.length}>
                        <StaffClaimList items={profile.good_at} />
                      </StaffSection>
                      <StaffSection title="Responsibilities" icon={Wrench} count={profile.responsibilities.length}>
                        <StaffClaimList items={profile.responsibilities} tone="plain" />
                      </StaffSection>
                      <StaffSection title="Communication style" icon={MessageSquare}>
                        <p className="text-sm text-glass-300 leading-7">
                          <EvidenceText text={profile.communication_style} />
                        </p>
                      </StaffSection>
                      <StaffSection title="Context" icon={Compass}>
                        <dl className="space-y-3">
                          {(
                            [
                              ['Current load', profile.current_load],
                              ['Activity pattern', profile.activity_pattern],
                            ] as [string, string][]
                          ).map(([k, v]) => (
                            <div key={k}>
                              <dt className="text-[10px] uppercase tracking-[0.14em] text-glass-500 mb-1">{k}</dt>
                              <dd className="text-sm text-glass-300 leading-7">
                                <EvidenceText text={v} />
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </StaffSection>
                    </div>

                    <div className="space-y-4 min-w-0">
                      <StaffSection title="Bot routes to them when" icon={Compass} count={profile.route_to_them_when.length}>
                        <StaffClaimList items={profile.route_to_them_when} />
                      </StaffSection>
                      <StaffSection title="Watch items" icon={Eye} count={profile.watch_items.length} tone="watch">
                        <StaffClaimList items={profile.watch_items} tone="watch" emptyMessage="No watch items recorded." />
                      </StaffSection>
                      <StaffSection title="Weaknesses" icon={ThumbsDown} count={profile.weaknesses.length} tone="poor">
                        <StaffClaimList items={profile.weaknesses} tone="poor" emptyMessage="No weaknesses recorded." />
                      </StaffSection>
                      <StaffSection title="Works with others" icon={Handshake} count={profile.collaboration.length}>
                        <StaffClaimList items={profile.collaboration} />
                      </StaffSection>
                      <StaffSection title="Bot routing rules" icon={SlidersHorizontal}>
                        <dl className="space-y-3">
                          {(
                            [
                              ['Prefers', profile.prefers],
                              ['Avoids', profile.avoids],
                              ['Do not route when', profile.do_not_route_when],
                            ] as [string, string[]][]
                          ).map(([label, items]) => (
                            <div key={label}>
                              <dt className="text-[10px] uppercase tracking-[0.14em] text-glass-500 mb-1">{label}</dt>
                              <dd>{items.length === 0 ? <span className="text-sm text-glass-500">— not enough signal yet</span> : <StaffClaimList items={items} tone="plain" />}</dd>
                            </div>
                          ))}
                        </dl>
                      </StaffSection>
                      <StaffSection title="Unknowns" icon={FileQuestion} count={profile.unknowns.length}>
                        <StaffClaimList items={profile.unknowns} tone="plain" />
                      </StaffSection>
                    </div>
                  </div>

                  <StaffSection title="Citation mix" icon={BarChart3} count={citationCount}>
                    <StaffCitationMix citations={data.citations ?? {}} />
                  </StaffSection>
                </>
              )}

              {data && (
                <>
                  <StaffSection title="Evidence log" icon={ScrollText} count={citationCount}>
                    <StaffEvidenceLog citations={data.citations ?? {}} generatedAt={data.generated_at} />
                  </StaffSection>
                  <p className="text-[11px] font-mono text-glass-500 leading-relaxed">
                    Snapshot generated {new Date(data.generated_at * 1000).toLocaleString()} · everything above is the steward bot's own automatic assessment, rendered verbatim; relative times are frozen at generation.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </StaffCitationsProvider>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => navigate('/staff')} className="glass-button-secondary inline-flex items-center gap-2 text-sm">
          <ArrowLeft className="w-4 h-4" />
          Back to roster
        </button>
        <button type="button" onClick={() => void load(true)} className="glass-button-secondary inline-flex items-center gap-2 text-sm" disabled={refreshing || loading}>
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>
      <div className="flex flex-col xl:flex-row gap-4 items-start">
        {rail}
        <div className="flex-1 min-w-0 w-full">{main}</div>
      </div>

      {entry && (
        <>
          <AssignToTeamModal
            isOpen={assignOpen}
            onClose={() => setAssignOpen(false)}
            teams={teams.filter((t) => !entry.teams.some((mt) => mt.id === t.id && isMembershipActive(mt, now)))}
            presetUser={{ id: entry.user_id, username: entry.username }}
            onSuccess={() => void load(true)}
          />
          <RemoveFromTeamModal isOpen={removeTarget !== null} onClose={() => setRemoveTarget(null)} team={removeTarget?.team ?? null} member={removeTarget?.member ?? null} teams={teams} onSuccess={() => void load(true)} />
          <MembershipExpiryModal isOpen={expiryTarget !== null} onClose={() => setExpiryTarget(null)} team={expiryTarget?.team ?? null} member={expiryTarget?.member ?? null} onSuccess={() => void load(true)} />
        </>
      )}
    </div>
  );
};
